import React, { useEffect, useRef, useState } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import Button from '@mui/material/Button';
import cn from 'clsx';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import useComponentUIHook from './useComponentUIHook';
import { SelectComponent, TextComponent, TooltipComponent } from '../../uiComponents';
import { SelectChangeEvent, Typography } from '@mui/material';
import { OptionProps } from '../../../view-models';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import { cloneDeep } from 'lodash';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import SessionManagement, { sessionFieldsAreValid } from './sessionManagement';
import jsonComponents from '../../../constants/jsonComponents';
import { FieldMapperRow } from './fieldMapperRow';
import TemplatizedFieldComponent, { TemplatizedValue, getTemplatizedValue } from './templatizedFieldComponent';

interface ComponentProps {
    componentData: any;
    closeGritComponent: any;
    setUnsavedChanges: any;
}

export const GenerateOIDCIdPUI = ({componentData, closeGritComponent, setUnsavedChanges}: ComponentProps) => {
    const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims, modifiedTemplatizations, setModifiedTemplatizations } = useComponentUIHook(componentData);

    const [claimsList, setClaimsList] = useState<OptionProps[]>([]);
    const [nextInputClaim, setNextInputClaim] = useState({
        claimId: '',
        partnerClaimType: ''
    });
    const [nextOutputClaim, setNextOutputClaim] = useState({
        claimId: '',
        partnerClaimType: '',
        defaultValue: '',
        alwaysUseDefaultValue: false
    });
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
    const configData = jsonComponents.oidcIdP.data;
    const classes = jsonEditorStyles();

    useEffect(() => {
        if (Object.keys(allClaims).length > 0) {
          const _temp = Object.keys(allClaims)
            .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
            .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
          setClaimsList(_temp);
        }
    }, [allClaims]);

    useEffect(() => {
        const _res: boolean = checkIfSameIgnoringSubFlows();
        setDisableSubmit(_res || (!modifiedData['issuerIdPartnerClaimType']) || (!modifiedData['metadataUrl']) || (!modifiedData['idpDomain']) || (!modifiedData['clientId']) || (!modifiedData['responseType']) || (!modifiedData['responseMode']) ||
            (!sessionFieldsAreValid(modifiedData)));
        setUnsavedChanges(_res);
    }, [modifiedData]);

    return (
        <div>
            <div className={cn(classes.flexRow)}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, idpDomain: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'idpDomain',
                        label: 'Identity Provider Domain',
                        required: true,
                        value: getTemplatizedValue(modifiedData['idpDomain']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="Identity Provider Domain"
                    desc="The domain ID of the identity provider (such as 'google.com'). OIDC IdP Authentication components with the same IdP domain will be assumed to refer to the same IdP. This domain may also be matched to a domain hint parameter."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, metadataUrl: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'metadataUrl',
                        label: 'OIDC Metadata URL',
                        required: true,
                        value: getTemplatizedValue(modifiedData['metadataUrl']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="OIDC Metadata URL"
                    desc="The URL of the identity provider's OIDC configuration document / OpenID well-known configuration endpoint."
                />
            </div>
            <div className={cn(classes.flexRow)} style={{marginLeft: '20px'}}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, authEndpointUrl: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'authEndpointUrl',
                        label: 'Authorization Endpoint URL',
                        value: getTemplatizedValue(modifiedData['authEndpointUrl']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="Authorization Endpoint URL"
                    desc="Optional: The URL of the identity provider's OIDC authorization endpoint. Takes precedence over a URL for the authorization endpoint returned from the provider's OpenID well-known configuration endpoint."
                />
            </div>
            <div className={cn(classes.flexRow)} style={{marginLeft: '20px'}}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, endSessionEndpointUrl: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'endSessionEndpointUrl',
                        label: 'End Session Endpoint URL',
                        value: getTemplatizedValue(modifiedData['endSessionEndpointUrl']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="End Session Endpoint URL"
                    desc="Optional: The URL of the identity provider's OIDC end session endpoint. Takes precedence over a URL for the end session endpoint returned from the provider's OpenID well-known configuration endpoint."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, clientId: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'clientId',
                        label: 'Client ID',
                        required: true,
                        value: getTemplatizedValue(modifiedData['clientId']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="Client ID"
                    desc="The ID assigned to your application registered with the identity provider."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                        setModifiedData({
                            ...modifiedData, responseType: e.target.value
                        })
                    }}
                    data={{
                        value: modifiedData['responseType'] ?? '',
                        label: 'Response Type',
                        options: [
                            { value: 'id_token', label: 'ID Token' },
                            { value: 'code', label: 'Code'}
                        ],
                        required: true,
                        style: { margin: 1, width: 500 }
                    }}
                />
                <TooltipComponent
                    title="Response Type"
                    desc="The type of response returned by the identity provider. A 'code' response type is used for the Authorization Code Flow, while an 'id_token' response type is used for the Implicit Flow."
                />
                {(modifiedData['responseType'] === 'code') && (
                    <>
                        <TemplatizedFieldComponent
                            changeHandler={(e: TemplatizedValue) => {
                                setModifiedData({
                                    ...modifiedData, clientSecretContainerName: { ...e }
                                });
                            }}
                            modifiedTemplatizations={modifiedTemplatizations}
                            setModifiedTemplatizations={setModifiedTemplatizations}
                            data={{
                                fieldPath: 'clientSecretContainerName',
                                label: 'Client Secret Container Name',
                                required: true,
                                value: getTemplatizedValue(modifiedData['clientSecretContainerName']),
                                style: { margin: 1, width: 700 },
                                variant: 'text'
                            }}
                        />
                        <TooltipComponent
                            title="Client Secret Container Name"
                            desc="The name of the key container on the ADB2C tenant containing the client secret for this application's registration with the identity provider."
                        />
                    </>
                )}
            </div>
            <div className={cn(classes.flexRow)}>
                <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                        setModifiedData({
                            ...modifiedData, responseMode: e.target.value
                        })
                    }}
                    data={{
                        value: modifiedData['responseMode'] ?? '',
                        label: 'Response Mode',
                        options: [
                            { value: 'form_post', label: 'POST' },
                            { value: 'query', label: 'URL'}
                        ],
                        required: true,
                        style: { margin: 1, width: 500 }
                    }}
                />
                <TooltipComponent
                    title="Response Mode"
                    desc="The method used by the identity provider to send the response back to ADB2C."
                />
            </div>
            <Typography variant='body1'>
                Input Claims 
            </Typography>
            <div style={{marginLeft: '20px'}}>
                {modifiedData['inputClaims'].map((icRef: any, icIdx: number) => {
                    return (
                        <FieldMapperRow
                            key={icIdx}
                            rowData={icRef}
                            writeback={(rowData) => {
                                const _inpClaims = modifiedData['inputClaims'].slice();
                                _inpClaims[icIdx] = rowData;
                                setModifiedData({
                                    ...modifiedData,
                                    inputClaims: _inpClaims
                                });
                            }}
                            claimsList={claimsList}
                            allClaims={allClaims}
                            enabledSources={['claim']}
                            fixedSourceType='claim'
                            sourceFieldName='claimId'
                            sourceFieldLabels={{claim: 'Claim'}}
                            partnerTypeFieldName='partnerClaimType'
                            partnerTypeFieldLabel='Partner Claim Type'
                            buttonText='Delete'
                            buttonAction={() => {
                                const _inpClaims = modifiedData['inputClaims'].filter((cl: any, clIdx: number) => { return clIdx !== icIdx });
                                setModifiedData({
                                    ...modifiedData,
                                    inputClaims: _inpClaims
                                });
                            }}
                        />
                    )
                })}
                <FieldMapperRow
                    rowData={nextInputClaim}
                    writeback={(rowData) => {
                        setNextInputClaim(rowData);
                    }}
                    claimsList={claimsList}
                    allClaims={allClaims}
                    enabledSources={['claim']}
                    fixedSourceType='claim'
                    sourceFieldName='claimId'
                    sourceFieldLabels={{claim: 'Claim'}}
                    partnerTypeFieldName='partnerClaimType'
                    partnerTypeFieldLabel='Partner Claim Type'
                    buttonText='Add'
                    buttonAction={() => {
                        const _inpClaims = modifiedData['inputClaims'].slice();
                        _inpClaims.push({ claimId: nextInputClaim.claimId, partnerClaimType: nextInputClaim.partnerClaimType});
                        setModifiedData({
                            ...modifiedData,
                            inputClaims: _inpClaims
                        });
                        setNextInputClaim({
                            claimId: '',
                            partnerClaimType: ''
                        });
                    }}
                    buttonDisabled={(!nextInputClaim.claimId) || (!nextInputClaim.partnerClaimType)}
                />
            </div>
            <Typography variant='body1'>
                Output Claims 
            </Typography>
            <div style={{marginLeft: '20px'}}>
                <div className={cn(classes.flexRow)}>
                    <TextComponent
                        changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                            setModifiedData({
                                ...modifiedData,
                                issuerIdPartnerClaimType: e.target.value
                            });
                        }}
                        data={{
                            label: 'Issuer User ID',
                            value: modifiedData['issuerIdPartnerClaimType'] ?? '',
                            required: true,
                            style: { margin: 1, width: 500 }
                        }}
                    />
                    <TooltipComponent
                        title="Issuer User ID"
                        desc="The partner claim type under which the identity provider will return its unique identifier for the user"
                    />
                </div>
                {modifiedData['outputClaims'].map((ocRef: any, ocIdx: number) => {
                    return (
                        <FieldMapperRow
                            key={ocIdx}
                            rowData={ocRef}
                            writeback={(rowData) => {
                                const _outClaims = modifiedData['outputClaims'].slice();
                                _outClaims[ocIdx] = rowData;
                                setModifiedData({
                                    ...modifiedData,
                                    outputClaims: _outClaims
                                });
                            }}
                            claimsList={claimsList.reduce((opts: OptionProps[], nxt: OptionProps) => {
                                if (!modifiedData['outputClaims'].find((oc: any) => { return oc.claimId === nxt.value })) {
                                  opts.push(nxt);
                                }
                                return opts;
                            }, [{ value: ocRef.claimId, label: ocRef.claimId }])}
                            allClaims={allClaims}
                            enabledSources={['claim']}
                            fixedSourceType='claim'
                            sourceFieldName='claimId'
                            sourceFieldLabels={{ claim: 'Claim' }}
                            partnerTypeFieldName='partnerClaimType'
                            partnerTypeFieldLabel='Partner Claim Type'
                            defaultValueFieldLabel='Default Value'
                            defaultValueFieldName='defaultValue'
                            allowEmptyDefaultValue={true}
                            alwaysUseDefaultValueFieldName='alwaysUseDefaultValue'
                            buttonText='Delete'
                            buttonAction={() => {
                                const _outClaims = modifiedData['outputClaims'].filter((cl: any, clIdx: number) => { return clIdx !== ocIdx });
                                setModifiedData({
                                    ...modifiedData,
                                    outputClaims: _outClaims
                                });
                            }}
                        />
                    )
                })}
                <FieldMapperRow
                    rowData={nextOutputClaim}
                    writeback={(rowData) => {
                        setNextOutputClaim(rowData);
                    }}
                    claimsList={claimsList.reduce((opts: OptionProps[], nxt: OptionProps) => {
                        if (!modifiedData['outputClaims'].find((oc: any) => { return oc.claimId === nxt.value })) {
                          opts.push(nxt);
                        }
                        return opts;
                    }, [])}
                    allClaims={allClaims}
                    enabledSources={['claim']}
                    fixedSourceType='claim'
                    sourceFieldName='claimId'
                    sourceFieldLabels={{claim: 'Claim'}}
                    partnerTypeFieldName='partnerClaimType'
                    partnerTypeFieldLabel='Partner Claim Type'
                    
                    defaultValueFieldLabel='Default Value'
                    defaultValueFieldName='defaultValue'
                    allowEmptyDefaultValue={true}
                    alwaysUseDefaultValueFieldName='alwaysUseDefaultValue'
                    buttonText='Add'
                    buttonAction={() => {
                        const _outClaims = modifiedData['outputClaims'].slice();
                        _outClaims.push({ claimId: nextOutputClaim.claimId, partnerClaimType: nextOutputClaim.partnerClaimType, defaultValue: nextOutputClaim.defaultValue, alwaysUseDefaultValue: nextOutputClaim.alwaysUseDefaultValue });
                        setModifiedData({
                            ...modifiedData,
                            outputClaims: _outClaims
                        });
                        setNextOutputClaim({
                            claimId: '',
                            partnerClaimType: '',
                            defaultValue: '',
                            alwaysUseDefaultValue: false
                        });
                    }}
                    buttonDisabled={(!nextOutputClaim.claimId) || ((!nextOutputClaim.partnerClaimType) && (!nextOutputClaim.defaultValue))}
                />
            </div>
            {configData.sessionManagement && (
                <SessionManagement
                    modifiedData={modifiedData}
                    changeHandler={(data: any) => setModifiedData(data)}
                    sessionTypes={configData.sessionTypes}
                />
            )}
            <div>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={disableSubmit}
                    sx={{ mt: 3, mb: 2 }}
                    onClick={() => {
                        closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
                    }}
                >
                    Save
                </Button>
            </div>
        </div>
    )
}